import React from 'react';
import { Box, Dialog, ProgressHero, Skeleton, Stack, Text, TextLink } from '@ads-core/components';
import { ChevronRightIcon, LoadIcon, NoElectricalConnectionIcon } from '@ads-core/icons';

import { IntegrationApiFeaturesCongestionReportsContractsResponsesGetCongestionReportsDetailsResponse as CongestionReportDetails } from '@alliander-fe/api';

import { Image } from 'src/components/Image';
import { setReplaceText } from 'src/utils';
import { getBandWidthDate } from 'src/utils/bandwidth/getBandwidthDate';

import { getReplaceText } from './utils/getReplaceText';
import { locationsFormatter } from './utils/locationFormatter';
import { setCongestionStatus } from './utils/setCongestionStatus';

import { RingsProps } from './hooks/useMapRings';

import { DialogContent } from './_DialogContent';
import { DialogPolygonMap } from './_DialogPolygonMap';
import { FALLBACK_STATUS, FALLBACK_VALUE } from './constant/constants';

import { CongestionReportHeroProps } from './';

type CongestionReportHeroViewProps = CongestionReportHeroProps & {
  mainTitle: string;
  isLoading: boolean;
  data?: CongestionReportDetails;
  rings: RingsProps;
};

export const CongestionReportHeroView = ({
  data,
  title,
  subTitle,
  topTasks,
  image,
  isLoading,
  statusLabel,
  congestionTypeLabel,
  statusPhaseOneLabel,
  statusPhaseOneText,
  statusPhaseTwoLabel,
  statusPhaseTwoText,
  statusPhaseThreeLabel,
  statusPhaseThreeText,
  detailsModalTitle,
  detailsModalTextLink,
  detailsModalButtonLabel,
  rings,
}: CongestionReportHeroViewProps) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const currentStatus = setCongestionStatus({
    status: data?.status ?? FALLBACK_STATUS,
    statusPhaseOneLabel: statusPhaseOneLabel?.toString() ?? '',
    statusPhaseOneText: statusPhaseOneText?.toString() ?? '',
    statusPhaseTwoLabel: statusPhaseTwoLabel?.toString() ?? '',
    statusPhaseTwoText: statusPhaseTwoText?.toString() ?? '',
    statusPhaseThreeLabel: statusPhaseThreeLabel?.toString() ?? '',
    statusPhaseThreeText: statusPhaseThreeText?.toString() ?? '',
  });

  const locations = locationsFormatter(data?.locations ?? FALLBACK_VALUE);
  const replaceText = getReplaceText(data);

  const mainTitle = setReplaceText(title?.toString() ?? '', replaceText);
  const detailTitle = setReplaceText(subTitle?.toString() ?? '', replaceText);
  const detailText = setReplaceText(currentStatus?.text ?? '', replaceText);
  const dialogTitle = setReplaceText(detailsModalTitle?.toString() ?? '', replaceText);
  const status = data?.status ? currentStatus?.label : FALLBACK_STATUS;

  const { resolvedStartDate: resolvedStartDate, resolvedEndDate: resolvedDate } = getBandWidthDate({
    resolvedStartDate: data?.resolvedStartDate,
    resolvedDate: data?.resolvedDate,
  });

  const { resolvedStartDate: resolvedStartDateMainAsset, resolvedEndDate: resolvedDateMainAsset } =
    getBandWidthDate({
      resolvedStartDate: data?.resolvedStartDateMainAsset,
      resolvedDate: data?.resolvedDateMainAsset,
    });

  const statusOptions = [statusPhaseOneLabel, statusPhaseTwoLabel, statusPhaseThreeLabel].filter(
    (val): val is string => val !== undefined
  );

  return (
    <ProgressHero
      detailDescription={detailText}
      detailTitle={detailTitle}
      status={currentStatus?.label ?? FALLBACK_VALUE}
      statusOptions={statusOptions}
      title={mainTitle}
      topTasks={topTasks ?? []}
      image={
        image?.src ? (
          <Image
            src={image.src}
            width={Number(image.width)}
            height={Number(image.height)}
            alt={image.alt}
          />
        ) : undefined
      }
      isLoading={isLoading}
      progressLink={
        <Dialog
          isOpen={isDialogOpen}
          handleOpenChange={setIsDialogOpen}
          trigger={
            <TextLink afterIcon={<ChevronRightIcon />} asChild>
              <button>Bekijk alle details</button>
            </TextLink>
          }
        >
          <DialogContent
            {...data}
            resolvedStartDate={resolvedStartDate}
            resolvedDate={resolvedDate}
            resolvedStartDateMainAsset={resolvedStartDateMainAsset}
            resolvedDateMainAsset={resolvedDateMainAsset}
            congestionManagementPublicationDate={data?.congestionManagementPublicationDate}
            report={data?.contentServerLink ?? undefined}
            statusLabel={status}
            locations={locations}
            detailsModalTitle={dialogTitle}
            detailsModalTextLink={detailsModalTextLink}
            detailsModalButtonLabel={detailsModalButtonLabel}
            handleCloseDialog={() => setIsDialogOpen(false)}
            map={<DialogPolygonMap {...rings} />}
          />
        </Dialog>
      }
      explanation={
        <Box paddingTop={{ initial: 4, md: 0 }} asChild>
          <Stack alignY="end" gap={2}>
            <Stack direction="row" alignY="center" gap={2}>
              <LoadIcon size="xs" />
              {isLoading ? (
                <Skeleton.Item width="80%" height="24px" />
              ) : (
                <Stack direction="row" gap={1} wrap>
                  <Text weight="semiBold">{statusLabel ?? 'Status'}:</Text>
                  <Text>{status}</Text>
                </Stack>
              )}
            </Stack>
            {isLoading ? (
              <Stack direction="row" alignY="center" gap={2}>
                <NoElectricalConnectionIcon size="xs" />
                <Skeleton.Item width="70%" height="24px" />
              </Stack>
            ) : (
              <Stack direction="row" alignY="center" gap={2}>
                <NoElectricalConnectionIcon size="xs" />
                <Stack direction="row" gap={1} wrap>
                  <Text weight="semiBold">{congestionTypeLabel?.toString() ?? 'Congestie'}:</Text>
                  <Text>{data?.congestionType ?? FALLBACK_VALUE}</Text>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>
      }
    />
  );
};
